import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPut } from '../../api/client';
import MediaPicker from '../../components/admin/MediaPicker';
import OptionGroup from './pageEditor/OptionGroup';
import { DragHandle, SortableItem, reorder } from './pageEditor/sortable';
import { HOME_BUTTON_STYLES, HOME_PLATFORMS, HOME_SECTION_TYPES, mergeHomeContent } from '../../data/homeDefaults';

const emptyHome = () => ({
  title: 'Home',
  seo: { description: '', keywords: '', url: 'https://zemaxi.com/' },
  content: mergeHomeContent(null),
});

const PAGE_TABS = [
  { id: 'home', label: 'Home', path: '/' },
  { id: 'about', label: 'About', path: '/about' },
  { id: 'stream', label: 'Stream Landing', path: '/stream' },
];

const emptyAbout = () => ({
  title: 'About ZeMaxi',
  seo: { description: '', keywords: '', url: '' },
  content: {
    heroSubtitle: '',
    profileImage: '',
    storyHeading: 'My Story',
    storyParagraphs: [],
    skillsHeading: 'Technical Skills',
    skillsIntro: '',
    skills: [],
  },
});

const emptyStream = () => ({
  title: 'Live Stream',
  seo: { description: '', keywords: '', url: '' },
  content: {
    heroSubtitle: '',
    offlineHeading: 'Currently Offline',
    offlineText: '',
    offlineLinks: [],
    platformHeading: 'Watch Live',
  },
});

function SeoFields({ page, onChange }) {
  const seo = page.seo || {};
  const setSeo = (patch) => onChange({ ...page, seo: { ...seo, ...patch } });

  return (
    <OptionGroup title="SEO" defaultOpen={false}>
      <label>
        Page title
        <input value={page.title || ''} onChange={(event) => onChange({ ...page, title: event.target.value })} />
      </label>
      <label>
        Description
        <textarea rows={3} value={seo.description || ''} onChange={(event) => setSeo({ description: event.target.value })} />
      </label>
      <label>
        Keywords
        <input value={seo.keywords || ''} onChange={(event) => setSeo({ keywords: event.target.value })} />
      </label>
      <label>
        Canonical URL
        <input value={seo.url || ''} onChange={(event) => setSeo({ url: event.target.value })} />
      </label>
    </OptionGroup>
  );
}

function AboutForm({ page, onChange }) {
  const content = page.content || {};
  const setContent = (patch) => onChange({ ...page, content: { ...content, ...patch } });
  const paragraphs = content.storyParagraphs || [];
  const skills = content.skills || [];

  return (
    <div className="admin-form">
      <OptionGroup title="Hero">
        <label>
          Subtitle
          <textarea rows={2} value={content.heroSubtitle || ''} onChange={(event) => setContent({ heroSubtitle: event.target.value })} />
        </label>
        <MediaPicker label="Profile image" value={content.profileImage || ''} onChange={(url) => setContent({ profileImage: url })} />
      </OptionGroup>
      <OptionGroup title="Story">
        <label>
          Story heading
          <input value={content.storyHeading || ''} onChange={(event) => setContent({ storyHeading: event.target.value })} />
        </label>
        {paragraphs.length === 0 && <p className="tpb-sidebar-hint">No story paragraphs yet.</p>}
        {paragraphs.map((paragraph, index) => (
          <SortableItem
            key={index}
            listId="about-paragraphs"
            index={index}
            className="tpb-repeat-block"
            onMove={(from, to) => setContent({ storyParagraphs: reorder(paragraphs, from, to) })}
          >
            <div className="tpb-nested-block-head">
              <DragHandle listId="about-paragraphs" index={index} />
              <strong>Paragraph {index + 1}</strong>
              <button type="button" className="admin-icon-btn danger" title="Remove paragraph" onClick={() => setContent({ storyParagraphs: paragraphs.filter((_, i) => i !== index) })}>
                <i className="fas fa-trash" aria-hidden="true" />
              </button>
            </div>
            <textarea
              rows={4}
              value={paragraph}
              onChange={(event) => {
                const storyParagraphs = [...paragraphs];
                storyParagraphs[index] = event.target.value;
                setContent({ storyParagraphs });
              }}
            />
          </SortableItem>
        ))}
        <button type="button" className="btn btn-secondary tpb-add-btn" onClick={() => setContent({ storyParagraphs: [...paragraphs, ''] })}>
          <i className="fas fa-plus" aria-hidden="true" /> Add paragraph
        </button>
      </OptionGroup>
      <OptionGroup title="Skills">
        <label>
          Skills heading
          <input value={content.skillsHeading || ''} onChange={(event) => setContent({ skillsHeading: event.target.value })} />
        </label>
        <label>
          Intro
          <textarea rows={2} value={content.skillsIntro || ''} onChange={(event) => setContent({ skillsIntro: event.target.value })} />
        </label>
        {skills.length === 0 && <p className="tpb-sidebar-hint">No skills yet.</p>}
        {skills.map((skill, index) => (
          <SortableItem
            key={index}
            listId="about-skills"
            index={index}
            className="tpb-repeat-block"
            onMove={(from, to) => setContent({ skills: reorder(skills, from, to) })}
          >
            <div className="tpb-nested-block-head">
              <DragHandle listId="about-skills" index={index} />
              <strong>{skill.label || `Skill ${index + 1}`}</strong>
              <button type="button" className="admin-icon-btn danger" title="Remove skill" onClick={() => setContent({ skills: skills.filter((_, i) => i !== index) })}>
                <i className="fas fa-trash" aria-hidden="true" />
              </button>
            </div>
            <div className="admin-grid-2">
              <label>
                Label
                <input value={skill.label || ''} onChange={(event) => {
                  const next = [...skills];
                  next[index] = { ...skill, label: event.target.value };
                  setContent({ skills: next });
                }} />
              </label>
              <label>
                Icon class
                <input value={skill.icon || ''} placeholder="fas fa-code" onChange={(event) => {
                  const next = [...skills];
                  next[index] = { ...skill, icon: event.target.value };
                  setContent({ skills: next });
                }} />
              </label>
            </div>
          </SortableItem>
        ))}
        <button type="button" className="btn btn-secondary tpb-add-btn" onClick={() => setContent({ skills: [...skills, { icon: 'fas fa-star', label: '' }] })}>
          <i className="fas fa-plus" aria-hidden="true" /> Add skill
        </button>
      </OptionGroup>
      <SeoFields page={page} onChange={onChange} />
    </div>
  );
}

function StreamLandingForm({ page, onChange }) {
  const content = page.content || {};
  const setContent = (patch) => onChange({ ...page, content: { ...content, ...patch } });
  const links = content.offlineLinks || [];

  return (
    <div className="admin-form">
      <OptionGroup title="Hero">
        <label>
          Subtitle
          <textarea rows={2} value={content.heroSubtitle || ''} onChange={(event) => setContent({ heroSubtitle: event.target.value })} />
        </label>
        <label>
          Platform heading
          <input value={content.platformHeading || ''} onChange={(event) => setContent({ platformHeading: event.target.value })} />
        </label>
      </OptionGroup>
      <OptionGroup title="Offline message">
        <label>
          Heading
          <input value={content.offlineHeading || ''} onChange={(event) => setContent({ offlineHeading: event.target.value })} />
        </label>
        <label>
          Text
          <textarea rows={3} value={content.offlineText || ''} onChange={(event) => setContent({ offlineText: event.target.value })} />
        </label>
        {links.length === 0 && <p className="tpb-sidebar-hint">No offline links yet.</p>}
        {links.map((link, index) => (
          <SortableItem
            key={index}
            listId="offline-links"
            index={index}
            className="tpb-repeat-block"
            onMove={(from, to) => setContent({ offlineLinks: reorder(links, from, to) })}
          >
            <div className="tpb-nested-block-head">
              <DragHandle listId="offline-links" index={index} />
              <strong>{link.label || `Link ${index + 1}`}</strong>
              <button type="button" className="admin-icon-btn danger" title="Remove link" onClick={() => setContent({ offlineLinks: links.filter((_, i) => i !== index) })}>
                <i className="fas fa-trash" aria-hidden="true" />
              </button>
            </div>
            <div className="admin-grid-2">
              <label>
                Label
                <input value={link.label || ''} onChange={(event) => {
                  const offlineLinks = [...links];
                  offlineLinks[index] = { ...link, label: event.target.value };
                  setContent({ offlineLinks });
                }} />
              </label>
              <label>
                Path
                <input value={link.href || ''} onChange={(event) => {
                  const offlineLinks = [...links];
                  offlineLinks[index] = { ...link, href: event.target.value };
                  setContent({ offlineLinks });
                }} />
              </label>
            </div>
          </SortableItem>
        ))}
        <button type="button" className="btn btn-secondary tpb-add-btn" onClick={() => setContent({ offlineLinks: [...links, { href: '/', label: '' }] })}>
          <i className="fas fa-plus" aria-hidden="true" /> Add link
        </button>
      </OptionGroup>
      <SeoFields page={page} onChange={onChange} />
    </div>
  );
}

function ButtonListFields({ listId, buttons, onChange }) {
  const list = buttons || [];
  const setButton = (index, patch) => {
    const next = [...list];
    next[index] = { ...list[index], ...patch };
    onChange(next);
  };

  return (
    <>
      {list.length === 0 && <p className="tpb-sidebar-hint">No buttons yet.</p>}
      {list.map((button, index) => (
        <SortableItem
          key={index}
          listId={listId}
          index={index}
          className="tpb-repeat-block"
          onMove={(from, to) => onChange(reorder(list, from, to))}
        >
          <div className="tpb-nested-block-head">
            <DragHandle listId={listId} index={index} />
            <strong>{button.label || `Button ${index + 1}`}</strong>
            <button type="button" className="admin-icon-btn danger" title="Remove button" onClick={() => onChange(list.filter((_, i) => i !== index))}>
              <i className="fas fa-trash" aria-hidden="true" />
            </button>
          </div>
          <div className="admin-grid-2">
            <label>
              Label
              <input value={button.label || ''} onChange={(event) => setButton(index, { label: event.target.value })} />
            </label>
            <label>
              Link
              <input value={button.href || ''} placeholder="/tools or https://…" onChange={(event) => setButton(index, { href: event.target.value })} />
            </label>
            <label>
              Icon class
              <input value={button.icon || ''} placeholder="fab fa-twitch" onChange={(event) => setButton(index, { icon: event.target.value })} />
            </label>
            <label>
              Style
              <select value={button.style || 'primary'} onChange={(event) => setButton(index, { style: event.target.value })}>
                {HOME_BUTTON_STYLES.map((style) => <option key={style} value={style}>{style}</option>)}
              </select>
            </label>
          </div>
        </SortableItem>
      ))}
      <button
        type="button"
        className="btn btn-secondary tpb-add-btn"
        onClick={() => onChange([...list, { label: '', href: '', icon: '', style: 'secondary' }])}
      >
        <i className="fas fa-plus" aria-hidden="true" /> Add button
      </button>
    </>
  );
}

function HomeForm({ page, onChange, products }) {
  const content = page.content || {};
  const hero = content.hero || {};
  const sections = content.sections || [];
  const tickerIds = hero.tickerProductIds || [];
  const productById = new Map(products.map((p) => [p.id, p]));
  const setHero = (patch) => onChange({ ...page, content: { ...content, hero: { ...hero, ...patch } } });
  const setSections = (next) => onChange({ ...page, content: { ...content, sections: next } });
  const setSection = (index, patch) => {
    const next = [...sections];
    next[index] = { ...sections[index], ...patch };
    setSections(next);
  };

  return (
    <div className="admin-form">
      <OptionGroup title="Hero">
        <p className="tpb-sidebar-hint">
          The status pill shows live/offline and your newest tool update. The cards on the right are your top three
          featured tools from the Tool Catalog. Keep the title short: the highlight is drawn in your theme colour.
        </p>
        <div className="admin-grid-2">
          <label>
            Title
            <input value={hero.title || ''} onChange={(event) => setHero({ title: event.target.value })} />
          </label>
          <label>
            Highlight
            <input value={hero.highlight || ''} onChange={(event) => setHero({ highlight: event.target.value })} />
          </label>
        </div>
        <div className="admin-grid-2">
          <label>
            Logo line prefix
            <input value={hero.platformsPrefix || ''} placeholder="for" onChange={(event) => setHero({ platformsPrefix: event.target.value })} />
          </label>
          <div>
            <p className="tpb-sidebar-hint">Platform logos (third line)</p>
            {Object.entries(HOME_PLATFORMS).map(([id, label]) => (
              <label className="admin-switch" key={id}>
                <input
                  type="checkbox"
                  checked={(hero.platforms || []).includes(id)}
                  onChange={(event) => setHero({
                    platforms: Object.keys(HOME_PLATFORMS).filter((p) => (p === id ? event.target.checked : (hero.platforms || []).includes(p))),
                  })}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </div>
        <label>
          Subtitle (optional)
          <textarea rows={2} value={hero.subtitle || ''} onChange={(event) => setHero({ subtitle: event.target.value })} />
        </label>
        <p className="tpb-sidebar-hint">Buttons</p>
        <ButtonListFields listId="home-hero-buttons" buttons={hero.buttons} onChange={(buttons) => setHero({ buttons })} />
      </OptionGroup>
      <OptionGroup title="Scrolling tool strip">
        <label className="admin-switch">
          <input type="checkbox" checked={Boolean(hero.showTicker)} onChange={(event) => setHero({ showTicker: event.target.checked })} />
          <span>Show the strip under the hero</span>
        </label>
        {hero.showTicker && (
          <>
            <p className="tpb-sidebar-hint">
              {tickerIds.length
                ? 'Only these tools scroll, in this order. Drag to reorder.'
                : 'No tools picked, so every tool in the catalog scrolls. Add tools to choose your own.'}
            </p>
            {tickerIds.map((id, index) => {
              const product = productById.get(id);
              return (
                <SortableItem
                  key={id}
                  listId="home-ticker"
                  index={index}
                  className="tpb-repeat-block admin-ticker-item"
                  onMove={(from, to) => setHero({ tickerProductIds: reorder(tickerIds, from, to) })}
                >
                  <div className="tpb-nested-block-head">
                    <DragHandle listId="home-ticker" index={index} />
                    {product?.overlayImage && <img src={product.overlayImage} alt="" className="admin-ticker-thumb" />}
                    <strong>{product ? product.name : 'Deleted tool'}</strong>
                    <button
                      type="button"
                      className="admin-icon-btn danger"
                      title="Remove from strip"
                      onClick={() => setHero({ tickerProductIds: tickerIds.filter((other) => other !== id) })}
                    >
                      <i className="fas fa-times" aria-hidden="true" />
                    </button>
                  </div>
                </SortableItem>
              );
            })}
            <div className="admin-grid-2">
              <label>
                Add tool
                <select
                  value=""
                  onChange={(event) => event.target.value && setHero({ tickerProductIds: [...tickerIds, event.target.value] })}
                >
                  <option value="">Choose a tool…</option>
                  {products.filter((p) => !tickerIds.includes(p.id)).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </label>
              {tickerIds.length > 0 && (
                <button type="button" className="btn btn-secondary tpb-add-btn" onClick={() => setHero({ tickerProductIds: [] })}>
                  Reset to all tools
                </button>
              )}
            </div>
          </>
        )}
      </OptionGroup>
      <OptionGroup title="Sections">
        <p className="tpb-sidebar-hint">
          Drag to reorder. Featured tools come from the Tool Catalog (★ and order), updates from Tool Updates,
          and the spotlight video from Videos.
        </p>
        {sections.map((section, index) => {
          const type = HOME_SECTION_TYPES[section.id] || {};
          return (
            <SortableItem
              key={section.id}
              listId="home-sections"
              index={index}
              className={`tpb-repeat-block${section.enabled ? '' : ' is-disabled'}`}
              onMove={(from, to) => setSections(reorder(sections, from, to))}
            >
              <div className="tpb-nested-block-head">
                <DragHandle listId="home-sections" index={index} />
                <strong><i className={type.icon} aria-hidden="true" /> {type.label}</strong>
                <label className="admin-switch">
                  <input type="checkbox" checked={Boolean(section.enabled)} onChange={(event) => setSection(index, { enabled: event.target.checked })} />
                  <span>Show</span>
                </label>
              </div>
              {section.enabled && (
                <>
                  <div className="admin-grid-2">
                    <label>
                      Eyebrow
                      <input value={section.eyebrow || ''} onChange={(event) => setSection(index, { eyebrow: event.target.value })} />
                    </label>
                    {type.hasCount && (
                      <label>
                        Items to show
                        <input type="number" min={1} max={24} value={section.count || 1} onChange={(event) => setSection(index, { count: Number(event.target.value) })} />
                      </label>
                    )}
                    <label>
                      Heading
                      <input value={section.heading || ''} onChange={(event) => setSection(index, { heading: event.target.value })} />
                    </label>
                    <label>
                      Highlight
                      <input value={section.highlight || ''} onChange={(event) => setSection(index, { highlight: event.target.value })} />
                    </label>
                  </div>
                  <label>
                    Intro
                    <textarea rows={2} value={section.intro || ''} onChange={(event) => setSection(index, { intro: event.target.value })} />
                  </label>
                  {section.id === 'cta' && (
                    <ButtonListFields listId="home-cta-buttons" buttons={section.buttons} onChange={(buttons) => setSection(index, { buttons })} />
                  )}
                </>
              )}
            </SortableItem>
          );
        })}
      </OptionGroup>
      <SeoFields page={page} onChange={onChange} />
    </div>
  );
}

export default function AdminPages() {
  const [activeTab, setActiveTab] = useState('home');
  const [editMode, setEditMode] = useState('form');
  const [home, setHome] = useState(emptyHome());
  const [about, setAbout] = useState(emptyAbout());
  const [stream, setStream] = useState(emptyStream());
  const [products, setProducts] = useState([]);
  const [jsonText, setJsonText] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    apiGet('/products').then(setProducts).catch(console.error);
    apiGet('/pages/home')
      .then((homePage) => setHome({ ...emptyHome(), ...homePage, content: mergeHomeContent(homePage.content) }))
      .catch(() => {});
    Promise.all([apiGet('/pages/about'), apiGet('/pages/stream')])
      .then(([aboutPage, streamPage]) => {
        setAbout({ ...emptyAbout(), ...aboutPage, content: { ...emptyAbout().content, ...(aboutPage.content || {}) } });
        setStream({ ...emptyStream(), ...streamPage, content: { ...emptyStream().content, ...(streamPage.content || {}) } });
      })
      .catch(console.error);
  }, []);

  const pages = {
    home: [home, setHome],
    about: [about, setAbout],
    stream: [stream, setStream],
  };
  const [page, setPage] = pages[activeTab];
  const activeMeta = PAGE_TABS.find((tab) => tab.id === activeTab);

  const showJson = () => {
    setJsonText(JSON.stringify(page, null, 2));
    setEditMode('json');
    setMessage('');
  };

  const applyJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      setPage(parsed);
      setEditMode('form');
      setMessage('JSON applied. Save to keep it.');
    } catch (err) {
      setMessage(`Invalid JSON: ${err.message}`);
    }
  };

  const saveActive = async () => {
    setMessage('');
    try {
      const current = editMode === 'json' ? JSON.parse(jsonText) : page;
      if (editMode === 'json') setPage(current);
      await apiPut(`/pages/${activeTab}`, {
        title: current.title,
        seo: current.seo,
        content: current.content,
      });
      setMessage(`${activeMeta.label} page saved.`);
    } catch (err) {
      setMessage(err.message);
    }
  };

  return (
    <div className="admin-panel-page">
      <div className="admin-panel-toolbar">
        <div className="admin-panel-toolbar-row">
          <div className="admin-panel-tabs">
            {PAGE_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`admin-panel-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => { setActiveTab(tab.id); setEditMode('form'); setMessage(''); }}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="admin-panel-toolbar-actions">
            <button type="button" className={`btn btn-secondary ${editMode === 'form' ? 'is-active' : ''}`} onClick={() => setEditMode('form')}>
              Form
            </button>
            <button type="button" className="btn btn-secondary" onClick={showJson}>JSON</button>
            <a href={activeMeta.path} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
              <i className="fas fa-external-link-alt" aria-hidden="true" /> Preview
            </a>
            <button type="button" className="btn btn-primary" onClick={saveActive}>
              Save {activeMeta.label}
            </button>
          </div>
        </div>
      </div>

      {activeTab === 'stream' && (
        <div className="admin-notice-banner">
          <p>
            Schedule, Stream Info, Setup, and custom pages are edited in{' '}
            <Link to="/admin/stream-pages">Stream Pages</Link>.
            Game calendar entries are managed in <Link to="/admin/schedule">Schedule Games</Link>.
          </p>
        </div>
      )}

      {message && <p className="admin-message">{message}</p>}

      {editMode === 'json' ? (
        <div className="admin-json-panel">
          <div className="admin-json-panel-head">
            <h3>JSON</h3>
            <button type="button" className="btn btn-primary" onClick={applyJson}>Apply JSON</button>
          </div>
          <textarea className="admin-json" value={jsonText} onChange={(event) => setJsonText(event.target.value)} rows={24} spellCheck={false} />
        </div>
      ) : (
        <div className="admin-json-panel">
          {activeTab === 'home' && <HomeForm page={home} onChange={setHome} products={products} />}
          {activeTab === 'about' && <AboutForm page={about} onChange={setAbout} />}
          {activeTab === 'stream' && <StreamLandingForm page={stream} onChange={setStream} />}
        </div>
      )}
    </div>
  );
}
